import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GitMerge, Search, Sparkles, Crown, X, Plus, RefreshCw, History } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const REF_LABEL = { visits: "visits", followups: "follow-ups", swots: "SWOTs", tasks: "tasks", facility_chats: "chats", facility_email_log: "emails" };

function RefCounts({ counts }) {
  const entries = Object.entries(counts || {}).filter(([, n]) => n > 0);
  if (!entries.length) return <span className="text-[11px] text-slate-400 italic">no linked records</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, n]) => (
        <span key={k} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-mono-plex">{n} {REF_LABEL[k] || k}</span>
      ))}
    </div>
  );
}

function FacilityChip({ f, primary, onMakePrimary, onRemove }) {
  return (
    <div className={`p-3 rounded-lg border transition-all ${primary ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-slate-200 bg-white"}`} data-testid={`merge-item-${f.facility_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 truncate">{f.name}</span>
            <Badge variant="outline" className="text-[9px]">{f.type}</Badge>
            {primary && <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[9px]"><Crown className="w-3 h-3 mr-1" /> Keep as primary</Badge>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{f.address ? `${f.address} · ` : ""}{f.city}{f.zip_code ? ` ${f.zip_code}` : ""} · {f.county}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{f.corporate_owner_name || "No owner"} · {f.assigned_rep_name || "Unassigned"} · <span className="uppercase">{f.referral_status?.replace("_", " ")}</span></div>
          <div className="mt-1.5"><RefCounts counts={f.ref_counts} /></div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {!primary && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onMakePrimary} data-testid={`merge-make-primary-${f.facility_id}`}>Make primary</Button>}
          {onRemove && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onRemove} data-testid={`merge-remove-${f.facility_id}`}><X className="w-3 h-3" /></Button>}
        </div>
      </div>
    </div>
  );
}

function MergeDialog({ group, open, onOpenChange, onMerged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [primaryId, setPrimaryId] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!group?.length) return;
    const best = [...group].sort((a, b) => sum(b.ref_counts) - sum(a.ref_counts))[0];
    setPrimaryId(best.facility_id);
  }, [group]);
  if (!group?.length) return null;
  const dups = group.filter(f => f.facility_id !== primaryId);
  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/facilities/merge", { primary_id: primaryId, duplicate_ids: dups.map(d => d.facility_id) });
      const moved = Object.entries(data.moved || {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${REF_LABEL[k] || k}`).join(", ");
      toast.success(`Merged ${data.merged_count} facilit${data.merged_count === 1 ? "y" : "ies"} into ${data.primary?.name}${moved ? ` · moved ${moved}` : ""}`);
      onOpenChange(false); onMerged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GitMerge className="w-5 h-5" /> Merge {group.length} facilities</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Pick the record to keep. Visits, follow-ups, SWOTs, tasks, chats and emails from the others are moved onto it, providers are combined, blank fields are filled in, and the old names are kept as searchable aliases. Duplicates are then deleted.</p>
        <div className="space-y-2">
          {group.map(f => <FacilityChip key={f.facility_id} f={f} primary={f.facility_id === primaryId} onMakePrimary={() => setPrimaryId(f.facility_id)} />)}
        </div>
        {!isAdmin && <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2" data-testid="merge-admin-only">Only admins can perform a merge. Ask your admin to complete this.</div>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={merge} disabled={busy || !isAdmin || dups.length === 0} className="bg-slate-900 hover:bg-slate-800" data-testid="merge-confirm-btn">
            <GitMerge className="w-4 h-4 mr-1" /> {busy ? "Merging…" : `Merge ${dups.length} into primary`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const sum = (c) => Object.values(c || {}).reduce((a, b) => a + b, 0);

export default function FacilitiesMerge({ onMerged }) {
  const [dupes, setDupes] = useState(null);
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  const [pickedDetail, setPickedDetail] = useState([]);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => {
    api.get("/facilities/duplicates").then(({ data }) => setDupes(data)).catch(() => setDupes({ groups: [] }));
    api.get("/facilities").then(({ data }) => { setAll(data); setHistory(data.filter(f => f.merged_from?.length)); });
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!picked.length) { setPickedDetail([]); return; }
    api.get("/facilities/merge-preview", { params: { ids: picked.join(",") } }).then(({ data }) => setPickedDetail(data.facilities));
  }, [picked]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return all.filter(f => !picked.includes(f.facility_id) && (f.name.toLowerCase().includes(s) || (f.city || "").toLowerCase().includes(s))).slice(0, 8);
  }, [q, all, picked]);

  const merged = () => { setPicked([]); setActive(null); load(); onMerged?.(); };

  return (
    <div className="space-y-6" data-testid="facilities-merge">
      <Card className="p-5 border-0 shadow-none bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 text-white">
        <div className="flex items-start gap-3">
          <GitMerge className="w-6 h-6 shrink-0 mt-0.5" />
          <div>
            <div className="font-heading font-bold text-lg">Merge duplicate facilities</div>
            <p className="text-sm text-white/80 mt-0.5">Facilities often appear under different names or get consolidated by their owner. Merge them so history, providers and follow-ups live on a single record.</p>
          </div>
        </div>
      </Card>

      <Card className="p-4 border-slate-200 shadow-none">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1"><Plus className="w-3 h-3" /> Manual merge</div>
            <h3 className="font-heading font-bold text-lg">Pick facilities to combine</h3>
          </div>
          {picked.length >= 2 && (
            <Button onClick={() => setActive(pickedDetail)} className="bg-slate-900 hover:bg-slate-800" data-testid="merge-picked-btn"><GitMerge className="w-4 h-4 mr-1" /> Review merge ({picked.length})</Button>
          )}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search a facility by name or city to add it…" className="pl-9" data-testid="merge-search" />
          {results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden" data-testid="merge-search-results">
              {results.map(f => (
                <button key={f.facility_id} onClick={() => { setPicked([...picked, f.facility_id]); setQ(""); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between" data-testid={`merge-pick-${f.facility_id}`}>
                  <span><span className="font-medium">{f.name}</span> <span className="text-slate-500 text-xs">· {f.city}</span></span>
                  <Badge variant="outline" className="text-[9px]">{f.type}</Badge>
                </button>
              ))}
            </div>
          )}
        </div>
        {picked.length === 0 && <div className="text-xs text-slate-400 italic mt-3">Add two or more facilities that are really the same place.</div>}
        {pickedDetail.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
            {pickedDetail.map(f => <FacilityChip key={f.facility_id} f={f} primary={false} onMakePrimary={null} onRemove={() => setPicked(picked.filter(p => p !== f.facility_id))} />)}
          </div>
        )}
      </Card>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Suggested</div>
            <h3 className="font-heading font-bold text-lg">Likely duplicates {dupes && <span className="text-slate-400 font-normal text-base">· {dupes.groups.length}</span>}</h3>
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="merge-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-scan</Button>
        </div>
        {!dupes && <div className="text-sm text-slate-400">Scanning…</div>}
        {dupes && dupes.groups.length === 0 && (
          <Card className="p-8 text-center border-dashed border-slate-200 shadow-none text-slate-400 text-sm" data-testid="merge-no-dupes">No likely duplicates found. Use the manual picker above if you know of any.</Card>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {dupes?.groups.map((g, i) => (
            <Card key={i} className="p-4 border-slate-200 shadow-none" data-testid={`dupe-group-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex flex-wrap gap-1">
                  {g.reasons.map(r => <span key={r} className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-mono-plex">{r}</span>)}
                </div>
                <Button size="sm" onClick={() => setActive(g.facilities)} className="bg-slate-900 hover:bg-slate-800 h-7 text-xs" data-testid={`dupe-merge-${i}`}><GitMerge className="w-3.5 h-3.5 mr-1" /> Merge</Button>
              </div>
              <div className="space-y-2">
                {g.facilities.map(f => (
                  <div key={f.facility_id} className="text-sm flex items-start justify-between gap-2 border-t border-slate-100 pt-2 first:border-0 first:pt-0">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{f.name}</div>
                      <div className="text-xs text-slate-500">{f.address ? `${f.address} · ` : ""}{f.city} · {f.type}</div>
                    </div>
                    <RefCounts counts={f.ref_counts} />
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {history.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1 mb-2"><History className="w-3 h-3" /> Merge history</div>
          <Card className="border-slate-200 shadow-none divide-y divide-slate-100" data-testid="merge-history">
            {history.map(f => (
              <div key={f.facility_id} className="p-3 text-sm flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-900">{f.name}</span>
                <span className="text-slate-400 text-xs">absorbed</span>
                {f.merged_from.map((m, i) => <span key={i} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]">{m.name}{m.city ? ` · ${m.city}` : ""}</span>)}
              </div>
            ))}
          </Card>
        </div>
      )}

      <MergeDialog group={active} open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }} onMerged={merged} />
    </div>
  );
}
